// 辣友會行動計劃場景庫——會員生命週期 12 幕（來源：docs/strategy/latyau-club-gtm.md v2.0 附錄 A）
// 照 godfather demo 哲學：圖畫表述思想，文字只是輔助（每幕一句 caption）
// 全部假數據零後端；色調 X-1493 紅 #d9381e；owner 標記 = GTM「零新開發優先」訊息
import type { ReactNode } from 'react';
import { Chat, QrImg } from './GfStoryPhone';

export interface ChiliScene {
  icon: string;
  node: string;        // FlowMap 節點標籤（2-4 字）
  caption: string;     // 一句 caption（文字輔助）
  phoneLabel: string;  // 手機框下標籤
  owner: 'auto' | 'manual' | 'new';  // 🟢 系統自動（現有引擎）／🟡 手動營運／🔵 需新建
  engine: string;      // 用哪個引擎
  phone: ReactNode;
}

const RED = '#d9381e';

/** WhatsApp 深色底容器 */
function Wa({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full flex-col bg-[#0b141a] px-3 pt-8">
      <div className="mb-2 text-center text-[9px] text-gray-500">WhatsApp</div>
      <div className="flex flex-1 flex-col justify-end gap-2 pb-4">{children}</div>
    </div>
  );
}

const sysBubble = 'demo-pop rounded-2xl bg-[#202c33] px-3 py-2.5 text-[10px] leading-relaxed text-gray-200 max-w-[88%]';

// ── 會員生命週期 12 幕 ──
export function chilifansScenes(): ChiliScene[] {
  return [
    {
      icon: '📣', node: '創會邀請', owner: 'auto', engine: '批量腳本＋客戶標籤',
      caption: 'D0：種子池專屬招募——50 位文記會員＋104 個活躍對話，比公開早 7 日。語氣是邀請，不是促銷。',
      phoneLabel: '種子池專屬邀請（D0）',
      phone: (
        <Wa>
          <div className={sysBubble}>
            🌶️ 陳生，你係第一批陪我走到今日嘅人——<br /><br />
            <b>辣友會創會會員招募</b>而家為你開放，比公開招募早 7 日。<br /><br />
            首 100 席：會籍卡刻名＋永久鎖定 HK$99/年＋首次聚會優先席位。
          </div>
          <div className="demo-pop w-[72%]" style={{ animationDelay: '.7s' }}>
            <div className="w-full rounded-lg bg-[#1f2c34] px-3 py-2 text-center text-[10px] font-bold text-[#53bdeb]">↗ 💳 創會會員 · 一鍵入會</div>
          </div>
        </Wa>
      ),
    },
    {
      icon: '💳', node: '一鍵入會', owner: 'new', engine: 'KPay Gateway＋/chiliclub 落地頁（新建）',
      caption: '按鈕直達 99 元 KPay 收銀台——付款→自動入會→自動歡迎旅程，全程零人手。',
      phoneLabel: '/chiliclub 落地頁',
      phone: (
        <div className="flex h-full flex-col bg-white px-4 pt-8">
          <div className="mb-2 text-center">
            <p className="text-[10px] font-bold" style={{ color: RED }}>🌶️ 辣友會 · 香港辣文化的會籍</p>
            <p className="text-[9px] text-gray-400">hkspicyvalley.com/chiliclub</p>
          </div>
          <div className="rounded-xl border-2 p-3" style={{ borderColor: RED }}>
            <p className="text-[11px] font-black text-gray-800">創會會員（首 100 席）</p>
            <p className="mt-0.5 text-[9px] leading-relaxed text-gray-500">每月辣中聖品寄到屋企＋全港合作商家專屬優惠＋會員群組＋季度聚會</p>
            <p className="mt-1 text-lg font-black" style={{ color: RED }}>HK$99<span className="text-[9px] font-normal text-gray-400">/年 · 永久鎖定</span></p>
          </div>
          <div className="mt-2 rounded-lg py-2.5 text-center text-[11px] font-bold text-white" style={{ background: RED }}>💳 KPay 一鍵入會</div>
          <p className="mt-1.5 text-center text-[8px] text-gray-400">付款→自動入會→歡迎旅程，全程零人手</p>
        </div>
      ),
    },
    {
      icon: '📲', node: '歡迎旅程', owner: 'auto', engine: 'godfather 歡迎旅程複用＋CMS 模板',
      caption: '入會即時：會籍確認＋辣中聖品首送預告＋會員群組邀請——互惠即刻回饋。',
      phoneLabel: '系統自動（入會即時）',
      phone: (
        <Wa>
          <div className={sysBubble}>
            🎉 歡迎加入辣友會，陳生！<br /><br />
            ✅ 創會會籍已生效（#003／100）<br />
            🌶️ 首盒辣中聖品 3 日內寄出<br />
            👥 會員群組邀請稍後送到<br /><br />
            由食客變辣友——歡迎返嚟 👊
          </div>
        </Wa>
      ),
    },
    {
      icon: '🌶️', node: '每月首送', owner: 'auto', engine: '郵寄引擎＋Label 列印',
      caption: '每月 D1 辣中聖品寄出通知（帶追蹤）；首送附實體會員卡——卡背 QR 直達會員賬戶。',
      phoneLabel: '系統自動（每月 D1）',
      phone: (
        <Wa>
          <div className={sysBubble}>
            📦 本月辣中聖品已寄出！<br /><br />
            🌶️ 10 月：文記辣椒油 ×4（獨立裝）<br />
            🚚 追蹤：SF 1234 5678 90<br />
            💳 實體會員卡隨盒附上——卡背 QR 直達你嘅會員賬戶<br /><br />
            月月唔同主題，月月有驚喜 😉
          </div>
        </Wa>
      ),
    },
    {
      icon: '🏪', node: '會員兌換', owner: 'auto', engine: '到店兌換閉環＋核銷即派券',
      caption: '每月一個會員專屬兌換活動：到店核銷即領體驗裝，該店優惠券自動入袋——即刻用得。',
      phoneLabel: '到店核銷（會員限定）',
      phone: (
        <div className="flex h-full flex-col bg-white px-4 pt-7">
          <div className="mb-2 rounded-lg px-3 py-2 text-center text-[10px] font-bold text-white" style={{ background: RED }}>
            🏪 會員專屬兌換 · 華星冰室
          </div>
          <div className="flex flex-col items-center py-1">
            <QrImg value="https://hkspicyvalley.com/chiliclub/redeem/DEMO" size={104} />
            <p className="mt-1.5 font-mono text-sm font-bold tracking-widest text-gray-800">LV003A</p>
            <p className="text-[9px] text-gray-400">到店出示——店員核銷即領</p>
          </div>
          <div className="demo-pop mt-1 rounded-lg bg-gray-50 p-2 text-[10px] leading-relaxed text-gray-600" style={{ animationDelay: '.5s' }}>
            🎁 核銷即領：本月體驗裝<br />
            🎉 本店優惠已自動入袋：麻辣厚多士半價
          </div>
        </div>
      ),
    },
    {
      icon: '📬', node: '優惠速遞', owner: 'auto', engine: 'CMS 模板＋批量腳本',
      caption: '每月 D5 本月優惠速遞：合作商家優惠合集——實用價值月月準時到。',
      phoneLabel: '系統自動（每月 D5）',
      phone: (
        <Wa>
          <div className={sysBubble}>
            📬 <b>本月優惠速遞</b>（10 月）<br /><br />
            🍞 華星冰室—麻辣厚多士半價<br />
            🍜 文記—辣椒油撈麵 85 折<br />
            🍲 觀塘麻辣燙—會員免費加湯<br /><br />
            出示會員卡即刻用得 ✅
          </div>
        </Wa>
      ),
    },
    {
      icon: '🎯', node: '季度辣任務', owner: 'auto', engine: 'godfather 引擎（分享任務 2.0）',
      caption: '每季一個辣任務：出帖 tag #辣友會 晒開箱——完成者下季配送加料（隱藏口味／限量聯乘醬）。',
      phoneLabel: '系統自動（季度）',
      phone: (
        <Wa>
          <div className={sysBubble}>
            🎯 <b>Q4 辣任務</b>開跑！<br /><br />
            📸 晒你嘅辣中聖品開箱<br />
            🏷️ Tag #辣友會 #香港辣谷<br /><br />
            完成者下季配送加料：隱藏口味＋限量聯乘醬 🔥
          </div>
        </Wa>
      ),
    },
    {
      icon: '🎉', node: '季度聚會', owner: 'manual', engine: '手動營運（線下活動）',
      caption: '會員帶一位非會員朋友——朋友現場入會享限定禮；「跟我一樣愛辣的人」歸屬感即時建立。',
      phoneLabel: '線下活動',
      phone: (
        <div className="flex h-full flex-col bg-white px-4 pt-8">
          <div className="flex h-24 items-center justify-center rounded-xl text-4xl" style={{ background: 'linear-gradient(135deg,#d9381e,#F4A261)' }}>🌶️🎉</div>
          <div className="mt-2">
            <p className="text-[12px] font-black text-gray-800">首屆辣友聚會</p>
            <p className="mt-0.5 text-[9px] text-gray-500">11 月 · 觀塘示範區 · 會員免費入場</p>
            <p className="mt-1 text-[9px] text-gray-500">每會員可帶一位朋友——朋友現場入會享聚會限定禮 🎁</p>
            <div className="mt-2 rounded-lg py-2 text-center text-[10px] font-bold text-white" style={{ background: RED }}>確認出席</div>
          </div>
        </div>
      ),
    },
    {
      icon: '💰', node: '帶友入會', owner: 'auto', engine: '推薦飛輪（10% 回贈引擎）',
      caption: '朋友經你專屬連結入會：你收 HK$20 現金回贈，朋友首年減 10 元——「朋友認證」雙向獎勵。',
      phoneLabel: '系統自動（推薦成交）',
      phone: (
        <Wa>
          <div className="demo-pop rounded-2xl bg-[#005c4b] px-3 py-2 text-[10px] text-white ml-auto max-w-[70%] rounded-tr-sm">
            阿明經我連結入咗會 🌶️
          </div>
          <div className={sysBubble} style={{ animationDelay: '.6s' }}>
            💰 <b>HK$20 現金回贈</b>已存入！<br /><br />
            你嘅朋友首年亦減咗 HK$10——<br />
            帶埋佢嚟季度聚會一齊玩 😉
          </div>
        </Wa>
      ),
    },
    {
      icon: '🔁', node: '回購喚醒', owner: 'auto', engine: 'workflow-cron C6（已上線照跑）',
      caption: '完成＋25 日 C6 回購推動照跑：會員 10% 回贈＋回憶殺——引擎已上線，零新開發。',
      phoneLabel: '系統自動（+25 日）',
      phone: (
        <Wa>
          <Chat
            text={'🌶️ 陳生，上次嗰盒辣椒油食完未？\n\n作為辣友會會員，你嘅專屬回饋：全單 10% 回贈，今個星期有效——'}
            buttons={['🛒 返 Store 補貨', '👤 睇我嘅會員賬戶']}
          />
        </Wa>
      ),
    },
    {
      icon: '👑', node: '享辣 IP', owner: 'manual', engine: '手動營運（內容孵化）',
      caption: '每月選「本月辣友」：內容指導＋食肆免費體驗——目標孵化 10 個辣內容創作者，成為辣界 KOL。',
      phoneLabel: '社群營運',
      phone: (
        <div className="flex h-full flex-col bg-white px-4 pt-8">
          <div className="mb-2 text-center">
            <p className="text-[10px] font-bold" style={{ color: RED }}>👑 本月辣友 · 10 月</p>
          </div>
          <div className="rounded-xl border border-gray-200 p-3 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full text-2xl" style={{ background: '#fdeae5' }}>📸</div>
            <p className="mt-1.5 text-[11px] font-black text-gray-800">@spicy.ah.cheung</p>
            <p className="text-[9px] text-gray-500">開箱帖 312 讚 · 轉發 47 次</p>
          </div>
          <div className="mt-2 rounded-lg bg-amber-50 p-2 text-[9px] leading-relaxed text-amber-800">
            🎁 獎勵：華星冰室免費體驗邀請＋攝影角度／文案套路 1 對 1 指導
          </div>
        </div>
      ),
    },
    {
      icon: '🎂', node: '周年續會', owner: 'auto', engine: 'CRM 標籤觸發（周年提醒）',
      caption: '會員周年提醒：創會會員永久鎖定 99 元——會籍價值隨商家網絡（8→80 店）一路成長。',
      phoneLabel: '系統自動（周年）',
      phone: (
        <Wa>
          <Chat
            text={'🎂 入會一周年快樂，陳生！\n\n創會會員價永久鎖定 HK$99/年。\n合作商家已經 12 間，仲喺度增加緊 📈\n\n下一盒辣中聖品等你續會解鎖——'}
            buttons={['↗ 一鍵續會 HK$99']}
          />
        </Wa>
      ),
    },
  ];
}
